import { useEffect, useMemo, useState } from 'react'
import { sectionUnitKey } from '@/shared/lib/sectionUnitKey'

/** Nur die Felder, die für die Schranke zählen - so bleibt der Hook frei von Typ-Importen. */
type PaywallArticle = {
  slug: string
  sections?: ReadonlyArray<{ id: string; topic?: string | null }> | null
}

type UseSciencePaywallParams = {
  isPaid: boolean | null
  /** Artikel, deren Karten im DOM hängen. Ihr Science-Abschnitt löst beim Scrollen aus. */
  visibleArticles: ReadonlyArray<PaywallArticle>
  /** Artikel, deren Science-Audio auslöst. Vorgabe: dieselben wie beim Scrollen. */
  audioArticles?: ReadonlyArray<PaywallArticle>
  /** Gerade spielender Abschnitt als unitKey, sonst null. */
  playingUnitKey?: string | null
  articleRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>
}

function scienceKeysOf(articles: ReadonlyArray<PaywallArticle>): string[] {
  const keys: string[] = []
  for (const article of articles) {
    if (!article?.slug) continue
    for (const section of article.sections || []) {
      if (section.topic === 'science')
        keys.push(sectionUnitKey(article.slug, section.id).toLowerCase())
    }
  }
  return keys
}

/**
 * Wann die Bezahlschranke aufgeht: sobald ein Science-Abschnitt von unten ins Bild
 * scrollt oder seine Audiodatei zu spielen beginnt. Bewusst nicht an einem allgemeinen
 * Scrollfortschritt festgemacht - so erscheint die Schranke in unterschiedlich langen
 * Artikeln immer an derselben inhaltlichen Stelle.
 *
 * Rückgabe ist die Deckkraft: 0 heißt verborgen, 1 voll sichtbar und bedienbar.
 */
export function useSciencePaywall({
  isPaid,
  visibleArticles,
  audioArticles,
  playingUnitKey,
  articleRefs,
}: UseSciencePaywallParams): number {
  const [opacity, setOpacity] = useState(0)

  const visibleKeys = useMemo(() => scienceKeysOf(visibleArticles), [visibleArticles])
  const audioKeys = useMemo(
    () => new Set(scienceKeysOf(audioArticles ?? visibleArticles)),
    [audioArticles, visibleArticles]
  )

  useEffect(() => {
    if (isPaid !== false) return
    if (opacity === 1) return
    if (visibleKeys.length === 0) return

    const els = visibleKeys
      .map((key) => articleRefs.current[key])
      .filter((el): el is HTMLDivElement => !!el)
    if (els.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setOpacity(1)
      },
      { threshold: 0 }
    )
    els.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [isPaid, opacity, visibleKeys, articleRefs])

  useEffect(() => {
    if (isPaid !== false) return
    if (opacity === 1) return
    if (!playingUnitKey) return
    if (audioKeys.has(playingUnitKey.toLowerCase())) setOpacity(1)
  }, [isPaid, opacity, playingUnitKey, audioKeys])

  return opacity
}
